// Vehicle catalog. Each vehicle has display + behavior hints used by scenes.

export type VehicleId =
  | "fire_truck" | "ambulance" | "police"
  | "excavator" | "bulldozer" | "crane"
  | "water_sprinkler" | "flatbed" | "mixer"
  | "road_roller" | "tractor" | "sports_car" | "big_truck";

export interface VehicleDef {
  id:    VehicleId;
  emoji: string;
  label: string;
  /** What this vehicle does when summoned in a fire scene. */
  job:   "extinguish" | "rescue" | "patrol" | "demolish" | "lift" | "transport" | "drive";
  /** How fast it crosses the screen (px/sec) */
  speed: number;
  /** Display scale */
  scale: number;
  /** Fallback video used if the card has no video_url set. Real cards override via admin. */
  sample_video?: string;
  /** Side-view sprite under web-game/public. When set the scene uses it; otherwise falls back to emoji. */
  sprite_url?: string;
}

// Google's public sample MP4s — used as placeholder fallbacks until real
// per-vehicle videos are uploaded to R2.
const G = (n: string) => `https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/${n}.mp4`;

// Vroom's own R2 bucket of curated kid-friendly vehicle videos.
const R2 = (n: string) => `https://pub-8b535a62cb5949abbda5d100d59fd4df.r2.dev/${n}`;

export const VEHICLES: Record<VehicleId, VehicleDef> = {
  fire_truck:      { id: "fire_truck",      emoji: "🚒",  label: "Fire Truck",      job: "extinguish", speed: 220, scale: 1.0,  sample_video: R2("fire_truck.mp4"),    sprite_url: "/sprites/fire_truck.png"      },
  ambulance:       { id: "ambulance",       emoji: "🚑",  label: "Ambulance",       job: "rescue",     speed: 260, scale: 1.0,  sample_video: R2("ambulance.mp4"),     sprite_url: "/sprites/ambulance.png"       },
  police:          { id: "police",          emoji: "🚓",  label: "Police",          job: "patrol",     speed: 280, scale: 1.0,  sample_video: R2("police.mp4"),        sprite_url: "/sprites/police.png"          },
  excavator:       { id: "excavator",       emoji: "🚜",  label: "Excavator",       job: "demolish",   speed: 140, scale: 1.1,  sample_video: R2("excavator.mp4"),     sprite_url: "/sprites/excavator.png"       },
  bulldozer:       { id: "bulldozer",       emoji: "🚧",  label: "Bulldozer",       job: "demolish",   speed: 120, scale: 1.1,  sample_video: R2("bulldozer.mp4"),     sprite_url: "/sprites/bulldozer.png"       },
  crane:           { id: "crane",           emoji: "🏗️",  label: "Crane",           job: "lift",       speed: 100, scale: 1.2,  sample_video: R2("crane.mp4"),         sprite_url: "/sprites/crane.png"           },
  water_sprinkler: { id: "water_sprinkler", emoji: "💧",  label: "Water Sprinkler", job: "extinguish", speed: 180, scale: 1.0,  sample_video: R2("water_sprinkler.mp4"), sprite_url: "/sprites/water_sprinkler.png" },
  flatbed:         { id: "flatbed",         emoji: "🛻",  label: "Flatbed",         job: "transport",  speed: 200, scale: 1.0,  sample_video: R2("flatbed.mp4"),       sprite_url: "/sprites/flatbed.png"         },
  mixer:           { id: "mixer",           emoji: "🛺",  label: "Cement Mixer",    job: "transport",  speed: 160, scale: 1.0,  sample_video: R2("mixer.mp4"),         sprite_url: "/sprites/mixer.png"           },
  road_roller:     { id: "road_roller",     emoji: "🛞",  label: "Road Roller",     job: "demolish",   speed: 90,  scale: 1.0,  sample_video: R2("road_roller.mp4"),   sprite_url: "/sprites/road_roller.png"     },
  tractor:         { id: "tractor",         emoji: "🚜",  label: "Tractor",         job: "drive",      speed: 140, scale: 0.95, sample_video: R2("tractor.mp4"),       sprite_url: "/sprites/tractor.png"         },
  sports_car:      { id: "sports_car",      emoji: "🏎️",  label: "Sports Car",      job: "drive",      speed: 360, scale: 0.95, sample_video: R2("sports_car.mp4"),    sprite_url: "/sprites/sports_car.png"      },
  big_truck:       { id: "big_truck",       emoji: "🚛",  label: "Big Truck",       job: "transport",  speed: 180, scale: 1.1,  sample_video: R2("big_truck.mp4"),     sprite_url: "/sprites/big_truck.png"       },
};

export function vehicleById(id: string | undefined): VehicleDef | null {
  if (!id) return null;
  return (VEHICLES as Record<string, VehicleDef>)[id] ?? null;
}

export const MAX_PER_TYPE = 10;
